// TDEE adaptatif empirique : maintenance réelle estimée depuis l'apport moyen
// et la variation de poids observée (Wishnofsky inversé) :
//   TDEE_réel = apport_moyen_quotidien + (Δpoids × 7700) / nb_jours

import { dailyCalories } from './calorie-analytics';
import { daysBetween } from './datetime';
import { mean, round } from './statistics';
import { KCAL_PER_KG_FAT } from './threshold';
import type { Consumption, WeightEntry } from './types';
import { smoothedWeight } from './weight-analytics';

export interface AdaptiveTdee {
  tdeeKcal: number;
  avgIntakeKcal: number;
  /** kg, négatif = perte. */
  weightDeltaKg: number;
  days: number;
  reliable: boolean;
}

// ~4 semaines lissent le bruit eau/glycogène.
export const ADAPTIVE_WINDOW_DAYS = 28;

// Renvoie null si données insuffisantes (< 2 pesées ou aucun apport saisi).
export function adaptiveTdee(
  consumptions: readonly Consumption[],
  weights: readonly WeightEntry[],
  windowDays = ADAPTIVE_WINDOW_DAYS,
): AdaptiveTdee | null {
  const smoothed = smoothedWeight(weights, 7);
  if (smoothed.length < 2) return null;

  const lastDate = smoothed[smoothed.length - 1]!.date;
  const inWindow = smoothed.filter((s) => daysBetween(s.date, lastDate) <= windowDays);
  if (inWindow.length < 2) return null;

  const first = inWindow[0]!;
  const last = inWindow[inWindow.length - 1]!;
  const days = daysBetween(first.date, last.date);
  if (days <= 0) return null;

  // Jours à 0 kcal = non suivis, exclus pour ne pas fausser la moyenne.
  const dailyIntakes = dailyCalories(consumptions, 0)
    .filter(
      (d) => d.kcal > 0 && daysBetween(d.date, lastDate) <= windowDays && d.date >= first.date,
    )
    .map((d) => d.kcal);
  if (dailyIntakes.length === 0) return null;

  const avgIntakeKcal = mean(dailyIntakes);
  const weightDeltaKg = last.smoothed - first.smoothed;
  const tdeeKcal = avgIntakeKcal - (weightDeltaKg * KCAL_PER_KG_FAT) / days;

  return {
    tdeeKcal: round(tdeeKcal),
    avgIntakeKcal: round(avgIntakeKcal),
    weightDeltaKg: round(weightDeltaKg, 2),
    days,
    reliable: days >= 14 && dailyIntakes.length >= 10,
  };
}
